import * as z from "zod";

/**
 * Every deployed extension function for the caller's team, newest first. A
 * "function" is a live `extension_target`: the host resource (a custom rate
 * table, an OMS code step) plus the contract its current artifact satisfies and
 * the most recent deployment that put it live.
 *
 * `health` is a thin run-status rollup over a recent window — `unknown` when the
 * function hasn't run in the window, `failing` when every run failed, `degraded`
 * when only some did, `healthy` when all succeeded. The full run log and richer
 * monitoring live behind a separate (later) surface.
 */
export const extensionTargetHealth = z.enum([
  "healthy",
  "degraded",
  "failing",
  "unknown",
]);

export type ExtensionTargetHealth = z.infer<typeof extensionTargetHealth>;

const extensionTargetItem = z.object({
  targetId: z.string(),
  targetType: z.string(),
  // Merchant-facing label for the target type (from the target definition).
  targetTypeLabel: z.string(),
  contractId: z.string(),
  currentArtifactId: z.string(),
  // Human name derived from where the function is called: for OMS code steps
  // `${flow name} / ${step title}`, for custom rates
  // `${experience name} / ${rate table name}`. See target-host.ts.
  name: z.string(),
  // Whether the function is actually wired into a live host config (not just
  // deployed). Resolved per target type — see function-liveness.ts.
  isLive: z.boolean(),
  liveReason: z.string(),
  // Where this function is edited, by host type. The frontend turns this into a
  // route (see function-edit.ts); null when there's no editable host.
  editContext: z
    .union([
      z.object({
        type: z.literal("checkoutExperience"),
        checkoutExperienceId: z.string(),
      }),
      z.object({
        type: z.literal("omsCodeStep"),
        flowId: z.string(),
        stepId: z.string(),
      }),
      z.object({ type: z.literal("market"), marketId: z.string() }),
      z.object({ type: z.literal("exchangeSettings") }),
      z.object({ type: z.literal("netSuiteIntegration") }),
      z.object({ type: z.literal("returnsAutomationSettings") }),
    ])
    .nullable(),
  // Run health over the last 24h. `health` buckets the failure rate for color;
  // `runCount`/`failureCount` are the raw numbers behind it so the UI can show
  // the actual rate. A "failure" is any non-success invocation status.
  health: extensionTargetHealth,
  runCount: z.number(),
  failureCount: z.number(),
  lastDeployedAt: z.string().nullable(),
  lastDeployedActor: z
    .union([
      // `name` is the deployer's resolved display name (name or email), or null
      // when the user lookup misses.
      z.object({
        type: z.literal("userId"),
        userId: z.string(),
        name: z.string().nullable(),
      }),
      z.object({ type: z.literal("admin"), email: z.string() }),
      z.object({ type: z.literal("agent"), agentId: z.string() }),
      z.object({ type: z.literal("system"), source: z.string() }),
    ])
    .nullable(),
  createdAt: z.string(),
});

export type ExtensionTargetListItem = z.infer<typeof extensionTargetItem>;

export const listExtensionTargets = {
  name: "listExtensionTargets" as const,
  input: z.object({}),
  output: z.object({ targets: z.array(extensionTargetItem) }),
};
